import styled, { keyframes } from "styled-components";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Container = styled.div`
  position: relative;
  border: solid 1px rgba(0, 0, 0, 0.1);
  padding: 4px;
  border-radius: 3px;
  border-left: solid 3px var(--blue);
  max-width: 500px;
  width: 100%;
  border-radius: 10px;
  background-color: #f4f2f2;
  cursor: ${({ $isLoading }) => ($isLoading ? "wait" : "pointer")};
  margin: 5px 0;
  transition: transform 0.12s ease, opacity 0.12s ease, background-color 0.12s ease;
  overflow: hidden;

  &:active {
    transform: ${({ $isLoading }) => ($isLoading ? "none" : "scale(0.98)")};
    opacity: ${({ $isLoading }) => ($isLoading ? 1 : 0.85)};
  }

  ${({ $isLoading }) =>
    $isLoading &&
    `
    opacity: 0.9;
    background-color: #ebe7f7;
  `}

  .dark & {
    background-color: #2a2d31;
    border-color: rgba(255, 255, 255, 0.1);

    ${({ $isLoading }) =>
      $isLoading &&
      `
      background-color: #322b45;
    `}
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  ${({ $isLoading }) => $isLoading && `filter: brightness(0.92);`}
`;

const Message = styled.span`
  width: 100%;
  flex: 1;
`;

const Img = styled.img`
  max-height: 200px;
  object-fit: contain;
  width: auto;
  pointer-events: none;
`;

const ThumbWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
`;

const Thumb = styled.img`
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 6px;
  flex-shrink: 0;
  pointer-events: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.72);
  z-index: 2;
  pointer-events: none;

  .dark & {
    background: rgba(20, 20, 24, 0.72);
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(124, 58, 237, 0.25);
  border-top-color: #7c3aed;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const LoadingLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  color: #7c3aed;

  .dark & {
    color: #c4b5fd;
  }
`;

const StoryWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 2px 0 8px;
  width: fit-content;
`;

const StoryLabel = styled.span`
  font-size: 11px;
  color: #8696a0;
  display: flex;
  align-items: center;
  gap: 5px;

  .dark & {
    color: #9ca3af;
  }
`;

const StoryContent = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
`;

const StoryBar = styled.div`
  width: 3px;
  border-radius: 3px;
  background: linear-gradient(180deg, #f58529, #dd2a7b, #8134af);
  flex-shrink: 0;
`;

const StoryThumb = styled.img`
  height: 110px;
  aspect-ratio: 9 / 16;
  object-fit: cover;
  border-radius: 10px;
  display: block;
`;

// Heuristica de base64: so aplicada a citacao de MIDIA (nunca a texto), entao
// nao ha risco de confundir uma frase longa com payload. Base64 aqui nao tem
// espacos nem pontuacao — a checagem no inicio da string ja separa os casos.
function looksLikeBase64(value) {
  if (typeof value !== "string" || value.length < 100) return false;
  const amostra = value.slice(0, 200).trim();
  return /^[A-Za-z0-9+/=]+$/.test(amostra);
}

export function QuotedMessage({ message }) {
  const { t } = useTranslation("chat");
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(null);

  useEffect(() => {
    const onStatus = (event) => {
      const { status, requestId } = event.detail || {};

      if (status === "idle") {
        setIsLoading(false);
        requestIdRef.current = null;
        return;
      }

      if (status === "loading") {
        if (requestId && requestId === requestIdRef.current) {
          setIsLoading(true);
        }
        return;
      }

      // done | error | ignored
      if (!requestIdRef.current) return;
      if (!requestId || requestId === requestIdRef.current) {
        setIsLoading(false);
        requestIdRef.current = null;
      }
    };
    window.addEventListener("navigateToMessageStatus", onStatus);
    return () => window.removeEventListener("navigateToMessageStatus", onStatus);
  }, []);

  function scrollToMessage(e) {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    if (isLoading) return;

    const messageId =
      message?.quotedMsgId ||
      message?.quotedMsg?.id ||
      message?.quotedStanzaID ||
      message?.quotedMsg?.stanzaId;

    if (!messageId && !message?.quotedMsg) return;

    const requestId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    requestIdRef.current = requestId;

    window.dispatchEvent(
      new CustomEvent("navigateToMessage", {
        detail: {
          messageId,
          quotedHint: message?.quotedMsg || null,
          requestId,
        },
      })
    );
  }

  function getTitle() {
    if (message?.quotedMsg?.fromMe) return t("header.you", "Você");
    const sender = (message?.sender?.name || message?.sender?.pushname || "").trim();
    if (sender && !message?.fromMe) return sender;
    return (message?.quotedParticipant || "").replace(/[@c.us, @g.us]/g, "");
  }

  const MEDIA_LABELS = {
    template: t("quotedMessage.mediaLabels.template", "Mensagem de modelo"),
    image: t("quotedMessage.mediaLabels.image", "📷 Imagem"),
    video: t("quotedMessage.mediaLabels.video", "🎥 Vídeo"),
    audio: t("quotedMessage.mediaLabels.audio", "🎤 Áudio"),
    ptt: t("quotedMessage.mediaLabels.audio", "🎤 Áudio"),
    document: t("quotedMessage.mediaLabels.document", "📎 Documento"),
    location: t("quotedMessage.mediaLabels.location", "📍 Localização"),
    sticker: t("list.sticker", "Figurinha"),
    vcard: t("quotedMessage.mediaLabels.contact", "👤 Contato"),
  };

  function renderQuotedContent() {
    const quoted = message?.quotedMsg;
    if (!quoted) return null;

    // Em citacao de MIDIA o `body` nao e texto: o WhatsApp manda ali a miniatura
    // em base64 (JPEG, inclusive para video e PDF). Usar body como texto fazia o
    // base64 inteiro aparecer escrito na conversa quando o tipo nao era imagem
    // (document/video caiam no fallback de texto). So citacao de texto tem body
    // legivel; nos demais tipos o texto vem apenas do caption.
    const isText = !quoted.type || quoted.type === "chat";
    const text = quoted.caption || (isText ? quoted.body : "");
    const isImage = quoted.type === "image" || quoted.mimetype === "image/jpeg";
    const thumbBase64 = !isText && looksLikeBase64(quoted.body) ? quoted.body : null;

    if (quoted.mediaUrl) {
      return <Img src={quoted.mediaUrl} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
    }

    if (isImage && looksLikeBase64(quoted.body)) {
      return <Img src={"data:image/jpeg;base64," + quoted.body} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
    }

    if (isText && text) {
      return <Message>{text}</Message>;
    }

    const label =
      (quoted.type === "template" && quoted.template?.templateName) ||
      MEDIA_LABELS[quoted.type] ||
      t("quotedMessage.mediaLabels.default", "Mensagem");

    // Video/PDF trazem miniatura no body: mostra a miniatura com o rotulo do
    // tipo ao lado (o caption, quando existe, entra no lugar do rotulo).
    if (thumbBase64) {
      return (
        <ThumbWrap>
          <Thumb src={"data:image/jpeg;base64," + thumbBase64} onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <Message>{text || label}</Message>
        </ThumbWrap>
      );
    }

    return <Message>{text || label}</Message>;
  }

  if (message?.quotedMsg?.isStory) {
    return (
      <StoryWrap>
        <StoryLabel>{t("quotedMessage.storyReplyLabel", "Respondeu ao story")}</StoryLabel>
        <StoryContent>
          <StoryBar />
          <StoryThumb
            src={message.quotedMsg.mediaUrl}
            alt={t("quotedMessage.storyAlt", "Story")}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </StoryContent>
      </StoryWrap>
    );
  }

  return (
    <Container $isLoading={isLoading} onClick={scrollToMessage} aria-busy={isLoading}>
      {isLoading && (
        <LoadingOverlay>
          <Spinner />
          <LoadingLabel>{t("messageList.quotedMessageJumping", "Abrindo mensagem...")}</LoadingLabel>
        </LoadingOverlay>
      )}
      <Content $isLoading={isLoading}>
        <span className="bg-black/5 dark:bg-white/10 p-0.5 rounded-sm">
          {getTitle()}
        </span>
        {renderQuotedContent()}
      </Content>
    </Container>
  );
}
